import { EmailTemplateTypes } from "../../../../common/constants"
import { AutomationSchedule, AutomationScheduleStep } from "../../../../domain/proposals/settings/proposalSettings"
import { uniqueId } from "../../../../helpers/uniqueId"
import { EmailTemplate } from "../../../EmailTemplates/emailTemplateModel"
import { defaultFollowUpEmailTemplates } from "../../../Proposals/proposalsModel"


type ViewStep = AutomationScheduleStep & {
  canDelete: boolean
  duplicateAfterDays: boolean
  displayDate: string
}

export type ViewSchedule = {
  canAddReminder: boolean
  steps: ViewStep[]
  hasErrors: boolean
  isEnabled: boolean
}

const MAX_STEPS = 3

export const defaultAutomationSchedule = (): AutomationSchedule => {
  return {
    steps: [
      { id: uniqueId(), type: 'follow-up', afterDays: 3, atTime: '12:00 PM', emailTemplateGuid: '00000000-0000-0000-0000-000000000000' },
      { id: uniqueId(), type: 'follow-up', afterDays: 6, atTime: '12:00 PM', emailTemplateGuid: '00000000-0000-0000-0000-000000000000' },
    ],
    enabled: false
  }
}

export const updateAfterDays = (schedule: AutomationSchedule, id: string, afterDays: number): AutomationSchedule => {
  afterDays = Math.max(1, Math.min(30, afterDays))
  return {
    ...schedule,
    steps: schedule.steps.map(x => x.id === id ? { ...x, afterDays } : x)
  }
}

export const updateAtTime = (schedule: AutomationSchedule, id: string, atTime: string): AutomationSchedule => {
  return {
    ...schedule,
    steps: schedule.steps.map(x => x.id === id ? { ...x, atTime } : x)
  }
}

export const addNewReminder = (schedule: AutomationSchedule): AutomationSchedule => {
  if (schedule.steps.length >= MAX_STEPS) {
    return schedule
  }
  const afterDays = Math.min(30, Math.max(...schedule.steps.map(x => x.afterDays)) + 1)
  return {
    ...schedule,
    steps: [...schedule.steps, { id: uniqueId(), type: 'follow-up', afterDays, atTime: '12:00 PM', emailTemplateGuid: '00000000-0000-0000-0000-000000000000' }]
  }
}

export const removeReminder = (schedule: AutomationSchedule, id: string): AutomationSchedule => {
  if (schedule.steps.length === 1) {
    return schedule
  }
  return {
    ...schedule,
    steps: schedule.steps.filter(x => x.id !== id)
  }
}

export const toViewSchedule = (schedule: AutomationSchedule): ViewSchedule => {
  const now = new Date()
  const view = {
    canAddReminder: schedule.steps.length < MAX_STEPS,
    steps: schedule.steps.map(step => ({
      ...step,
      canDelete: schedule.steps.length > 1,
      duplicateAfterDays: schedule.steps.filter(x => x.afterDays == step.afterDays).length > 1,
      displayDate: calculateViewDate(now, step.afterDays)
    })),
    isEnabled: schedule.enabled
  }
  return {...view, hasErrors: view.steps.some(x => x.duplicateAfterDays)}
}

export const calculateViewDate = (start: Date, afterDays: number): string => {
  const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + afterDays)
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })
}

export function setEmailTemplateGuid(schedule: AutomationSchedule, id: string, emailTemplateGuid: string) {
  return {
    ...schedule,
    steps: schedule.steps.map(step => {
      if (step.id === id) {
        return {...step, emailTemplateGuid: emailTemplateGuid}
      } else {
        return step
      }
    })
  }
}

export const setEnabled = (schedule: AutomationSchedule, enabled: boolean): AutomationSchedule =>{
  return {
    ...schedule,
    enabled
  }
}

export const getFollowUpTemplateOptions = (templates: EmailTemplate[], index: number): {label: string, value: string}[] => {
  let options = [...templates]
  if (defaultFollowUpEmailTemplates[index]) {
    options = [
      defaultFollowUpEmailTemplates[index],
      ...options
    ]
  }

  return options.filter(x => x.type === EmailTemplateTypes.Proposal).map(e => ({
    label: e.name,
    value: e.guid
  }))
}

export const findSelectedTemplate = (selectedId: string, index: number, templates: EmailTemplate[]): EmailTemplate => {
  return templates.find(x => x.guid === selectedId) ?? defaultFollowUpEmailTemplates[index]
}